<script setup lang="ts">
import { onMounted } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'

// TODO(backend): replace with a real XRP Ledger confirmation webhook poll.
// Today this fakes the success transition after a short delay so the demo
// flow stays moving.
const MOCK_CONFIRMATION_DELAY_MS = 1000

defineProps<{
  submittedAt?: string
}>()

const emit = defineEmits<{
  (e: 'confirmed'): void
}>()

onMounted(() => {
  setTimeout(() => emit('confirmed'), MOCK_CONFIRMATION_DELAY_MS)
})
</script>

<template>
  <Box align="start" gap="6" class="twp">
    <Box direction="col" align="center" justify="center" gap="4" class="twp__left">
      <Box flex align="center" justify="center" class="twp__spinner-wrap">
        <Box class="twp__spinner" />
      </Box>
      <Text type="display-lg" align="center" class="twp__title">Awaiting Confirmation</Text>
      <Text type="caption" color="muted" align="center" class="twp__desc">
        Trustline transaction submitted. Waiting for network confirmation on the XRP Ledger.
      </Text>
    </Box>

    <Box direction="col" justify="center" grow class="twp__right">
      <Box direction="col" gap="6" class="twp__card">
        <Text type="page-title" transform="uppercase" class="twp__section-title">
          Trustline Details
        </Text>

        <Box direction="col" gap="2" class="twp__details">
          <Box align="center" justify="between">
            <Text type="body" color="muted">Submitted</Text>
            <Text type="heading" weight="medium">{{ submittedAt ?? '—' }}</Text>
          </Box>
          <Box class="twp__divider" />
          <Box align="center" justify="between">
            <Text type="body" color="muted">Network</Text>
            <Text type="heading" weight="medium">XRP Ledger Mainnet</Text>
          </Box>
          <Box class="twp__divider" />
          <Box align="center" justify="between">
            <Text type="body" color="muted">Status</Text>
            <Text type="heading" weight="medium" class="twp__status-pending">Pending</Text>
          </Box>
        </Box>

        <Box align="start" gap="2" class="twp__info-banner">
          <Text is="span" class="twp__info-icon">ℹ️</Text>
          <Text type="caption" color="muted" class="twp__info-text">
            XRP Ledger confirmations typically take 3–5 seconds. The page will update automatically
            once confirmed.
          </Text>
        </Box>
      </Box>
    </Box>
  </Box>
</template>

<style scoped>
.twp {
  width: 100%;
}

.twp__left {
  flex: 1;
  padding: 1.25rem;
  text-align: center;
  min-height: 280px;
}

.twp__spinner-wrap {
  width: 50px;
  height: 50px;
}

.twp__spinner {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  border: 3px solid var(--p-content-border-color);
  border-top-color: var(--p-primary-600);
  animation: twp-spin 0.9s linear infinite;
}

@keyframes twp-spin {
  to {
    transform: rotate(360deg);
  }
}

.twp__title {
  line-height: 38px !important;
  margin: 0;
}

.twp__desc {
  max-width: 300px;
  margin: 0;
}

.twp__card {
  background: var(--p-surface-0);
  border-radius: var(--p-border-radius-lg);
  padding: 1.25rem;
}

.twp__details {
  border: 1px solid var(--p-content-border-color);
  border-radius: var(--p-border-radius-lg);
  padding: 18px 1.25rem;
}

.twp__divider {
  height: 1px;
  background: var(--p-surface-100);
}

.twp__status-pending {
  color: var(--p-warning-700) !important;
}

.twp__info-banner {
  background: var(--p-surface-100);
  border: 1px solid var(--p-content-border-color);
  border-radius: var(--p-border-radius-md);
  padding: 13px 17px;
}

.twp__info-icon {
  font-size: 14px;
  flex-shrink: 0;
  line-height: 18px;
}

.twp__info-text {
  line-height: 18px !important;
}

@media (max-width: 992px) {
  .twp {
    flex-direction: column;
    gap: 1.25rem;
  }

  .twp__right {
    width: 100%;
  }
}
</style>
